import type { CustomerFileCategory } from '@/api/portalFiles';

/** Customer-facing guidance for one upload category: why we need it,
 *  the columns we usually see in the file, and (for the spreadsheet
 *  categories) a downloadable example template served from
 *  `web/public/templates/`. Rendered inside every upload section —
 *  wizard steps and the dashboard alike. */
export interface UploadGuidance {
  /** Plain-language what-and-why, written for the customer. */
  why: string;
  /** Columns usually present in the files we need (spreadsheet categories). */
  columns?: string[];
  /** Accepted-formats hint for the document categories. */
  formats?: string;
  /** Path to the downloadable example, under the app origin. */
  templatePath?: string;
}

export const UPLOAD_GUIDANCE: Record<CustomerFileCategory, UploadGuidance> = {
  general_ledger: {
    why: 'Your full transaction export is what our engine analyzes — every recoverable dollar we find starts here. Export it from your accounting system for the whole engagement period.',
    columns: [
      'Transaction date',
      'Document number',
      'Vendor / supplier name',
      'Account',
      'Memo',
      'Amount',
      'Tax amount',
      'Currency',
    ],
    templatePath: '/templates/general-ledger-template.csv',
  },
  chart_of_accounts: {
    why: 'Your account list tells us what each GL account means, so expenses get classified the way your books intend.',
    columns: ['Account number', 'Account name', 'Account type'],
    templatePath: '/templates/chart-of-accounts-template.csv',
  },
  invoice: {
    why: 'Supplier invoices are the evidence behind recoveries — the CRA needs them to support a claim. Whatever export you have works: PDFs, scans, or photos.',
    formats: 'PDF, JPG/PNG images, or spreadsheets — one file per invoice is ideal.',
  },
  returns: {
    why: 'GST/HST returns already filed for the period (and any working copies) show what was claimed, so we only pursue what was missed.',
    formats: 'PDF returns plus any spreadsheet working copies.',
  },
  other: {
    why: 'Anything else that helps — statements, working papers, correspondence. When in doubt, upload it.',
    formats: 'Any file type.',
  },
};
